import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useDebounce} from '@/lib/useDebounce';
import {isoDateDaysAgo} from '@/components/submissions/SubmissionFilters';

// The grid is an infinite scroll over every submission ever made, so on a mature deployment the
// default view is tens of thousands of rows an analyst scrolls past to reach this week's work.
// Open on the last 30 days instead. It is a normal date-range filter, not a hidden cap: it shows
// as a removable chip and "Clear all" drops it, so widening to all of history is one click.
const DEFAULT_DATE_WINDOW_DAYS = 30;

// `from` absent means the default window above, so a cleared lower bound has to be written
// explicitly or the next render would put the window straight back.
const NO_DATE_FROM = 'any';

// Multi-select filters, each a repeated URL key (`?status=sent&status=failed`). `policy_violation`,
// `status` and `q` are the keys the dashboard and older links already use.
const LIST_KEYS = {
  policyViolation: 'policy_violation',
  status: 'status',
  submissionType: 'type',
  analyst: 'analyst',
  language: 'language',
  topic: 'topic',
  location: 'location',
  risk: 'risk',
  platformStatus: 'platform_status',
  customerFeedback: 'customer_feedback',
  flag: 'flag'
} as const;

// Tri-state filters: `true`, `false`, or absent for Any.
const TRI_STATE_KEYS = {
  hasCalibration: 'has_calibration',
  hasCalibrationNotes: 'has_calibration_notes',
  hasReviewerComments: 'has_reviewer_comments',
  thumbnailChanged: 'thumbnail_changed'
} as const;

type ListKey = (typeof LIST_KEYS)[keyof typeof LIST_KEYS];
type TriStateKey = (typeof TRI_STATE_KEYS)[keyof typeof TRI_STATE_KEYS];

function readTriState(value: string | null): boolean | undefined {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
}

// Every Submissions filter, held in the URL so a view survives a reload and can be linked to.
// Writes replace the history entry, so Back leaves the page rather than stepping through filters.
// `query` is the search box as typed; `debouncedQuery` is the settled value in `?q`.
export function useSubmissionUrlFilters(focusSubmissionId: string | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [query, setQuery] = useState(q);
  const debouncedQuery = useDebounce(query, 300);

  // The params as last written, so two writes in one event (a date preset sets `from` and clears
  // `to`) build on each other instead of both starting from the render's snapshot, where the
  // second would undo the first. react-router's functional setSearchParams does not queue.
  const latest = useRef(searchParams);
  useEffect(() => {
    latest.current = searchParams;
  }, [searchParams]);

  const update = useCallback(
    (change: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(latest.current);
      change(next);
      latest.current = next;
      setSearchParams(next, {replace: true});
    },
    [setSearchParams]
  );

  // Follow a q changed from outside (a link, browser back, Clear all). Our own write always lands
  // as q === debouncedQuery, so skipping that case never clobbers what the analyst is still typing.
  useEffect(() => {
    if (q !== debouncedQuery) {
      setQuery(q);
    }
    // Only an external q change should resync; debouncedQuery is read, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    if (debouncedQuery === q) {
      return;
    }

    update((next) => {
      if (debouncedQuery) {
        next.set('q', debouncedQuery);
      } else {
        next.delete('q');
      }
    });
    // Only a settled search should write the URL; `update` closes over the current params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Memoised on the params so each array keeps its identity until the URL actually changes, which
  // is what the list's fetch effect keys on.
  const values = useMemo(() => {
    const list = (key: ListKey) => searchParams.getAll(key).filter(Boolean);
    const triState = (key: TriStateKey) => readTriState(searchParams.get(key));
    const rawFrom = searchParams.get('from');
    return {
      // Arriving on a specific submission overrides the default window: the whole point of the
      // link is to land on that row, and silently filtering it out because it is 31 days old would
      // be worse than showing more than usual.
      dateFrom:
        rawFrom === null
          ? focusSubmissionId
            ? ''
            : isoDateDaysAgo(DEFAULT_DATE_WINDOW_DAYS)
          : rawFrom === NO_DATE_FROM
            ? ''
            : rawFrom,
      dateTo: searchParams.get('to') ?? '',
      policyViolationFilter: list(LIST_KEYS.policyViolation),
      statusFilter: list(LIST_KEYS.status),
      submissionTypeFilter: list(LIST_KEYS.submissionType),
      analystFilter: list(LIST_KEYS.analyst),
      languageFilter: list(LIST_KEYS.language),
      topicFilter: list(LIST_KEYS.topic),
      locationFilter: list(LIST_KEYS.location),
      riskFilter: list(LIST_KEYS.risk),
      platformStatusFilter: list(LIST_KEYS.platformStatus),
      customerFeedbackFilter: list(LIST_KEYS.customerFeedback),
      flagFilter: list(LIST_KEYS.flag),
      hasCalibrationFilter: triState(TRI_STATE_KEYS.hasCalibration),
      hasCalibrationNotesFilter: triState(TRI_STATE_KEYS.hasCalibrationNotes),
      hasReviewerCommentsFilter: triState(TRI_STATE_KEYS.hasReviewerComments),
      thumbnailChangedFilter: triState(TRI_STATE_KEYS.thumbnailChanged)
    };
  }, [searchParams, focusSubmissionId]);

  const setters = useMemo(() => {
    const setList = (key: ListKey) => (next: string[]) => {
      update((params) => {
        params.delete(key);
        for (const value of next) {
          params.append(key, value);
        }
      });
    };

    const setTriState = (key: TriStateKey) => (next: boolean | undefined) => {
      update((params) => {
        if (next === undefined) {
          params.delete(key);
        } else {
          params.set(key, String(next));
        }
      });
    };

    return {
      setDateFrom(next: string) {
        update((params) => {
          params.set('from', next || NO_DATE_FROM);
        });
      },
      setDateTo(next: string) {
        update((params) => {
          if (next) {
            params.set('to', next);
          } else {
            params.delete('to');
          }
        });
      },
      // One write for both ends: two separate writes in the same event would each start from the
      // same params, and the second would undo the first.
      clearDates() {
        update((params) => {
          params.set('from', NO_DATE_FROM);
          params.delete('to');
        });
      },
      // Drops every filter, including the default date window. Leaves the deep-link keys
      // (`submission`, `p0For`) alone: they pick a row or a dialog, not what the list shows.
      clearAll() {
        update((params) => {
          params.delete('q');
          for (const key of [...Object.values(LIST_KEYS), ...Object.values(TRI_STATE_KEYS)]) {
            params.delete(key);
          }

          params.set('from', NO_DATE_FROM);
          params.delete('to');
        });
      },
      setPolicyViolationFilter: setList(LIST_KEYS.policyViolation),
      setStatusFilter: setList(LIST_KEYS.status),
      setSubmissionTypeFilter: setList(LIST_KEYS.submissionType),
      setAnalystFilter: setList(LIST_KEYS.analyst),
      setLanguageFilter: setList(LIST_KEYS.language),
      setTopicFilter: setList(LIST_KEYS.topic),
      setLocationFilter: setList(LIST_KEYS.location),
      setRiskFilter: setList(LIST_KEYS.risk),
      setPlatformStatusFilter: setList(LIST_KEYS.platformStatus),
      setCustomerFeedbackFilter: setList(LIST_KEYS.customerFeedback),
      setFlagFilter: setList(LIST_KEYS.flag),
      setHasCalibrationFilter: setTriState(TRI_STATE_KEYS.hasCalibration),
      setHasCalibrationNotesFilter: setTriState(TRI_STATE_KEYS.hasCalibrationNotes),
      setHasReviewerCommentsFilter: setTriState(TRI_STATE_KEYS.hasReviewerComments),
      setThumbnailChangedFilter: setTriState(TRI_STATE_KEYS.thumbnailChanged)
    };
  }, [update]);

  return {query, setQuery, debouncedQuery: q, ...values, ...setters};
}
